import React, { Suspense, createElement, memo, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useExpanded, useFlexLayout, useResizeColumns, useRowSelect, useSortBy, useTable } from 'react-table';

import Button from 'components/field/Button';
import CardViewer from 'components/field/CardViewer';
import PickupAndDeliveryType from 'components/field/PickupAndDeliveryType';
import Status, {
	OTOLinkSessionStatus,
	OtoOrderStatusHistory,
	ShipmentStatus,
	TransactionStatus
} from 'components/field/Status';
import Checkbox from 'components/global/Checkbox';
import Icon from 'components/global/Icon';
import InvoiceLink from 'components/global/InvoiceLink';
import ListProfileImage from 'components/global/ListProfileImage';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useMediaQuery from 'hooks/useMediaQuery';
import useUpdateEffect from 'hooks/useUpdateEffect';

import DownloadImportFileButton from 'pages/Settings/ImportExport/DownloadImportFileButton';
import ExportFileStatus from 'pages/Settings/ImportExport/ExportFileStatus';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faAngleDown, faAngleUp, faEllipsis, faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';

import DynamicListColumnSummary from './DynamicListColumnSummary';
import DynamicListContextMenu from './DynamicListContextMenu';
import DynamicListExportButton from './DynamicListExportButton';
import DynamicListFilter from './DynamicListFilter';
import { FilterType, ListComponents, convertFilterValueToDTO } from './DynamicListFilterUtils';
import DynamicListPagination from './DynamicListPagination';
import DynamicListRowMenu from './DynamicListRowMenu';
import * as S from './DynamicListTable.styled';
import HeaderFilter from './HeaderFilter';
import ChipViewer from './HelperComponents/ChipViewer';
import CityTags from './HelperComponents/CityTags';
import CountryViewer from './HelperComponents/CountryViewer';
import CurrencyViewer from './HelperComponents/CurrencyViewer';
import CurrencyViewerBold from './HelperComponents/CurrencyViewerBold';
import CurrencyViewerColored from './HelperComponents/CurrencyViewerColored';
import DateTimeViewer from './HelperComponents/DateTimeViewer';
import DateTimeWithoutLocaleViewer from './HelperComponents/DateTimeWithoutLocaleViewer';
import DateViewer from './HelperComponents/DateViewer';
import DownloadFile from './HelperComponents/DownloadFile';
import ExpiryDateViewer from './HelperComponents/ExpiryDateViewer';
import ExportFileName from './HelperComponents/ExportFileName';
import LastComment from './HelperComponents/LastComment';
import {
	BrandLogoViewer,
	DCLogoViewer,
	DeliveryCompanyLogoViewer,
	ProductLogoViewer,
	SalesChannelLogoViewer
} from './HelperComponents/LogoViewer';
import PhoneViewer from './HelperComponents/PhoneViewer';
import StringArray from './HelperComponents/StringArray';
import TrackingURL from './HelperComponents/TrackingURL';
import UrlViewer from './HelperComponents/UrlViewer';
import WarehouseWorkingStatus from './HelperComponents/WarehouseWorkingStatus';
import { setDynamicListExporting } from './dynamicListSlice';

export const DynamicListComponentMap = {
	[ListComponents.STATUS]: {
		component: ({ value, column }) => <Status value={value} prefix={column?.statusPrefix} />
	},
	[ListComponents.EXPORT_FILE_STATUS]: {
		component: ExportFileStatus,
		requiredFields: ['filePath', 'requestDate', 'name', 'expired'],
		exportFields: ['filePath']
	},
	[ListComponents.IMPORT_FILE_STATUS]: {
		component: ({ value }) => <Status value={`importStatus.${value ?? 'pending'}`} />
	},
	[ListComponents.DOWNLOAD_IMPORT_FILE_BUTTON]: {
		component: DownloadImportFileButton,
		requiredFields: ['filePath'],
		exportFields: ['filePath']
	},
	[ListComponents.SHIPMENT_STATUS]: {
		component: ({ value, data, column }) => (
			<ShipmentStatus
				value={value}
				data={data}
				prefix={column?.statusPrefix}
				renderCellProps={column?.renderCellProps}
				statusHistoryApi={column?.statusHistoryApi}
			/>
		),
		requiredFields: ['ID', 'orderID']
	},
	[ListComponents.OTO_ORDER_STATUS_HISTORY]: {
		component: ({ value, data, column }) => (
			<OtoOrderStatusHistory value={value} data={data} prefix={column?.statusPrefix} />
		),
		requiredFields: ['otoOrderId']
	},
	[ListComponents.WAREHOUSE_WORKING_STATUS]: {
		component: WarehouseWorkingStatus,
		requiredFields: ['pickupLocationStatus', 'operationalHours']
	},
	[ListComponents.DATE]: { component: DateViewer },
	[ListComponents.DATETIME]: { component: DateTimeViewer },
	[ListComponents.DATETIMEWITHOUTLOCALE]: { component: DateTimeWithoutLocaleViewer },
	[ListComponents.EXPIRY_DATE]: { component: ExpiryDateViewer },
	[ListComponents.TEXT_ARRAY]: { component: ({ value }) => <Typography>{value?.join(', ')}</Typography> },
	[ListComponents.STRING_ARRAY]: { component: StringArray },
	[ListComponents.PROFILE_IMAGE]: { component: ListProfileImage, requiredFields: ['online', 'avatarPath'] },
	[ListComponents.PERCENTAGE]: { component: ({ value }) => <Typography>{value}%</Typography> },
	[ListComponents.PRODUCT_IMAGE]: {
		component: ProductLogoViewer,
		requiredFields: ['imageURL'],
		exportFields: ['imageURL']
	},
	[ListComponents.BRAND_LOGO]: { component: BrandLogoViewer, requiredFields: ['brandLogo'] },
	[ListComponents.DC_LOGO]: {
		component: DCLogoViewer,
		requiredFields: ['deliveryCompanyLogo', 'isReverseShipment']
	},
	[ListComponents.DELIVERY_COMPANY_LOGO]: {
		component: DeliveryCompanyLogoViewer,
		requiredFields: ['icon', 'name']
	},
	[ListComponents.SALES_CHANNEL_LOGO]: {
		component: SalesChannelLogoViewer,
		requiredFields: ['salesChannelLogo']
	},
	[ListComponents.TRACKING_URL]: {
		component: TrackingURL,
		requiredFields: ['trackingURL'],
		exportFields: ['trackingURL']
	},
	[ListComponents.LAST_COMMENT]: {
		component: LastComment,
		requiredFields: ['lastComment']
	},
	[ListComponents.PHONE]: { component: PhoneViewer },
	[ListComponents.CARD]: {
		component: ({ data }) => <CardViewer lastFour={data.cardLastFour} cardBrand={data.cardBrand} />,
		requiredFields: ['cardBrand']
	},
	[ListComponents.CURRENCY]: {
		component: ({ value, data, column }) => (
			<CurrencyViewer value={value} data={data} renderCellProps={column?.renderCellProps} />
		),
		requiredFields: ['currency'],
		exportFields: ['currency']
	},
	[ListComponents.CURRENCY_COLORED]: {
		component: CurrencyViewerColored,
		requiredFields: ['currency'],
		exportFields: ['currency']
	},
	[ListComponents.CURRENCY_BOLD]: {
		component: CurrencyViewerBold,
		requiredFields: ['currency'],
		exportFields: ['currency']
	},
	[ListComponents.DOWNLOAD_INVOICE]: { component: InvoiceLink, requiredFields: ['ID'] },
	[ListComponents.COUNTRY]: { component: CountryViewer },
	[ListComponents.URL]: { component: UrlViewer },
	[ListComponents.DOWNLOAD_FILE]: { component: DownloadFile },
	[ListComponents.EXPORT_FILE_NAME]: {
		component: ExportFileName,
		requiredFields: ['translationObject']
	},
	[ListComponents.TRANSACTION_STATUS]: {
		component: ({ value, data, column }) => (
			<TransactionStatus value={value} data={data} prefix={column?.statusPrefix} />
		),
		requiredFields: ['ID', 'orderID']
	},
	[ListComponents.OTO_LINK_SESSION_STATUS]: {
		component: ({ value, data, column }) => (
			<OTOLinkSessionStatus value={value} data={data} prefix={column?.statusPrefix} />
		),
		requiredFields: ['linkSessionId']
	},
	[ListComponents.CHIP_VIEWER]: {
		component: ChipViewer
	},
	[ListComponents.CLIENT_AGREEMENT_TYPE]: {
		component: ({ value }) => <Typography>{`clientAgreementType.${value}`}</Typography>
	},
	[ListComponents.PICKUP_AND_DELIVERY_TYPE]: {
		component: ({ value }) => <PickupAndDeliveryType value={value} />
	},
	[ListComponents.CITY_TAGS]: { component: CityTags }
};

/**
 * @author Burak Unsal
 */
const DynamicListTable = (props) => {
	const {
		id,
		fetchUrl,
		data,
		dataSize,
		pageSize,
		currentPage,
		selectionModel,
		onPageChange,
		onPageSizeChange,
		onSelectionModelChange,
		sortModel,
		onSortModelChange,
		filterModel,
		onFilterModelChange,
		defaultSort = [],
		errorMsg,
		noRecordsFound,
		loading,
		showHeader,
		exportName,
		showCheckbox,
		showMapViewButton,
		columns,
		originalColumns,
		setColumns,
		hiddenColumns,
		setHiddenColumns,
		actionsMenu,
		showFilters,
		showHeaderFilters,
		exportNameTranslationObject,
		infiniteScroll,
		isInfiniteScrollBottomLoading,
		isInfiniteScrollTopLoading,
		noMoreInfiniteScrollRecords,
		handleInfiniteScrollTop,
		handleInfiniteScrollBottom,
		disableInfiniteScrollUp,
		showColumnCalculation,
		requestParameters
	} = props;
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const isExporting = useSelector((state) => state.dynamicListData.isExporting);

	/**
	 * @description These components must take 'value' or 'data' as a prop and return a React element. Data prop contains row data. Value prop contains the value of the cell.
	 */

	const showActionsMenu = actionsMenu && Array.isArray(actionsMenu) && actionsMenu.length > 0;

	const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });
	const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
	const [selectedContextMenuRow, setSelectedContextMenuRow] = useState(null);

	const [copyValue, setCopyValue] = useState(null);
	const [copyColumnValues, setCopyColumnValues] = useState(null);

	const isMobile = useMediaQuery(Breakpoints.mobile);
	const multiSelectFilters = useMemo(
		() =>
			filterModel
				?.filter((filter) => filter.column.filterVisible === false || filter.column.filterRemovable === false)
				?.map((filter) => convertFilterValueToDTO(filter, false)),
		[filterModel]
	);

	const defaultColumn = useMemo(
		() => ({
			minWidth: isMobile ? 30 : 45,
			maxWidth: 500
		}),
		[isMobile]
	);

	useEffect(() => {
		let loadingTimeout;

		if (isExporting) {
			loadingTimeout = setTimeout(() => {
				dispatch(setDynamicListExporting(false));
			}, 5000);
		}

		return () => {
			clearTimeout(loadingTimeout);
		};
	}, [isExporting]);

	// initialize selected rows
	const selectedIdsInitState = useMemo(() => {
		if (selectionModel.length === 0) return {};

		const temp = {};

		if (selectionModel.length >= pageSize) {
			data.forEach((row, index) => {
				selectionModel.forEach((selectedRow) => {
					if (JSON.stringify(row) === JSON.stringify(selectedRow)) {
						temp[index] = true;
					}
				});
			});
		} else {
			selectionModel.forEach((selectedRow) => {
				data.forEach((row, index) => {
					if (JSON.stringify(row) === JSON.stringify(selectedRow)) {
						temp[index] = true;
					}
				});
			});
		}

		return temp;
	}, [selectionModel, pageSize, data]);

	const loadingData = useMemo(() => [...Array(Number(pageSize))].map(() => ({})), [pageSize]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		footerGroups,
		rows,
		prepareRow,
		visibleColumns,
		setHiddenColumns: setTableHiddenColumns,
		state: { selectedRowIds, sortBy, hiddenColumns: hiddenColumnsState },
		toggleAllRowsSelected
	} = useTable(
		{
			columns,
			data: loading ? loadingData : data,
			defaultColumn,
			manualSortBy: true,
			autoResetPage: false,
			autoResetHiddenColumns: false,
			initialState: {
				selectedRowIds: selectedIdsInitState,
				sortBy: defaultSort,
				hiddenColumns
			}
		},
		useFlexLayout,
		useResizeColumns,
		useSortBy,
		useExpanded,
		useRowSelect,
		(hooks) => {
			hooks.visibleColumns.push((columns) => {
				const visibleColumnsTemp = [];

				if (showActionsMenu || showCheckbox) {
					visibleColumnsTemp.push({
						id: 'rowSelection',
						Header: ({ getToggleAllRowsSelectedProps }) => (
							<div className="center">
								<Checkbox {...getToggleAllRowsSelectedProps()} />
							</div>
						),
						Cell: ({ row }) => (
							<div className="center">
								<Checkbox {...row.getToggleRowSelectedProps()} />
							</div>
						),
						className: 'row-selection',
						width: 30,
						maxWidth: 30
					});
				}
				visibleColumnsTemp.push(...columns);
				if (showActionsMenu) {
					visibleColumnsTemp.push({
						id: 'rowActions',
						Header: '',
						Cell: ({ row }) => (
							<DynamicListRowMenu
								buttonIcon={faEllipsis}
								menuItems={actionsMenu}
								row={row.original}
								onClose={() => {
									setIsContextMenuOpen(false);
									setSelectedContextMenuRow(null);
									setCopyValue(null);
									setCopyColumnValues(null);
								}}
							/>
						),
						className: 'row-actions',
						width: 30
					});
				}
				return visibleColumnsTemp;
			});
		}
	);

	useEffect(() => {
		const willSelectRows = Object.keys(selectedRowIds).filter(
			(id) => !Object.keys(selectedIdsInitState).includes(id)
		);
		const willUnselectRows = Object.keys(selectedIdsInitState).filter(
			(id) => !Object.keys(selectedRowIds).includes(id)
		);

		let selectedData = [...selectionModel];

		willSelectRows.forEach((id) => {
			selectedData.push(data[Number(id)]);
		});

		willUnselectRows.forEach((id) => {
			selectedData = selectedData.filter((row) => JSON.stringify(row) !== JSON.stringify(data[Number(id)]));
		});

		if (JSON.stringify(selectedData) !== JSON.stringify(selectionModel)) {
			onSelectionModelChange(selectedData);
		}
	}, [selectedRowIds]);

	const mobileExpandButtonCellId = useMemo(
		() => (isMobile ? visibleColumns.findIndex((column) => column.showOnMobile) : -1),
		[isMobile, showActionsMenu, showCheckbox, visibleColumns]
	);

	useEffect(() => {
		if (hiddenColumnsState !== hiddenColumns) {
			setHiddenColumns(hiddenColumnsState);
		}
	}, [hiddenColumnsState]);

	useUpdateEffect(() => {
		if (sortBy?.length) {
			const sortObj = { key: [sortBy[0].id], order: sortBy[0].desc ? 'desc' : 'asc' };
			onSortModelChange(sortObj);
		} else if (sortModel) {
			onSortModelChange(undefined);
		}
	}, [sortBy]);

	const renderTableHeader = (column) => {
		if (column.id === 'rowSelection' || column.id === 'rowActions') {
			return column.render('Header');
		}
		return t(column.Header);
	};

	const handleOnContextMenu = (event, row, isResizerSpace) => {
		if (isMobile) return;
		event.preventDefault();
		event.stopPropagation();
		setAnchorPoint({ x: event.pageX, y: event.pageY });
		setSelectedContextMenuRow(row.original);
		handleCopyMenu(event, isResizerSpace);
		setIsContextMenuOpen(true);
	};

	const handleCopyMenu = (event, isResizerSpace) => {
		const target = isResizerSpace ? event.target.previousElementSibling : event.target;

		const value = target.innerText;
		if (!value) return;

		const isTooltip = target.classList?.contains('tooltip') || target.classList?.contains('tooltip-text');
		if (isTooltip) return;

		try {
			const cell = target.closest('.td');
			const row = cell.closest('.tr');
			const cellIndex = Array.prototype.indexOf.call(row.children, cell);
			const tbody = target.closest('.tbody');
			const rows = tbody.querySelectorAll('.tr');
			const columnValues = Array.from(rows).map((row) => row.children[cellIndex].innerText);
			event.preventDefault();
			event.stopPropagation();
			setAnchorPoint({ x: event.pageX, y: event.pageY });
			setCopyValue(value);
			setCopyColumnValues(columnValues);
		} catch (error) {
			AppLogger.error(error);
		}
	};

	const tableRef = useRef(null);

	const [infiniteScrollTimestamp, setInfiniteScrollTimestamp] = useState(null);

	const handleInfiniteWheel = (event) => {
		if (isInfiniteScrollTopLoading || isInfiniteScrollBottomLoading) return;

		const isScrollingDown = event.deltaY > 0;

		if (isScrollingDown && !noMoreInfiniteScrollRecords) {
			const scrolledBottom =
				Math.abs(
					tableRef.current.scrollHeight - (tableRef.current.scrollTop + tableRef.current.clientHeight)
				) <= 100;

			if (scrolledBottom) {
				handleInfiniteScrollBottom();
			}
			return;
		}

		if (disableInfiniteScrollUp) return;

		const isScrollingUp = event.deltaY < 0;

		if (isScrollingUp) {
			// check if time passed since last refresh is more than 30 seconds
			if (infiniteScrollTimestamp !== null && Date.now() - infiniteScrollTimestamp < 30000) return;

			const scrolledTop = tableRef.current.scrollTop < 50;
			if (scrolledTop) {
				setInfiniteScrollTimestamp(Date.now());
				handleInfiniteScrollTop();
			}
		}
	};

	const [lastScrollTop, setLastScrollTop] = useState(0);

	const handleInfiniteScroll = (event) => {
		if (isInfiniteScrollTopLoading || isInfiniteScrollBottomLoading) return;
		setLastScrollTop(event.target.scrollTop);

		const isScrollingDown = event.target.scrollTop > lastScrollTop;

		if (isScrollingDown && !noMoreInfiniteScrollRecords) {
			const scrolledBottom =
				Math.abs(event.target.scrollHeight - (event.target.scrollTop + event.target.clientHeight)) <= 100;

			if (scrolledBottom) {
				handleInfiniteScrollBottom();
			}
			return;
		}

		if (disableInfiniteScrollUp) return;

		const isScrollingUp = event.target.scrollTop < lastScrollTop;

		if (isScrollingUp) {
			// check if time passed since last refresh is more than 30 seconds
			if (infiniteScrollTimestamp !== null && Date.now() - infiniteScrollTimestamp < 30000) return;

			const scrolledTop = event.target.scrollTop < 50;
			if (scrolledTop) {
				setInfiniteScrollTimestamp(Date.now());
				handleInfiniteScrollTop();
			}
		}
	};

	return (
		<>
			<S.Container className={`${id}-table`}>
				<DynamicListFilter
					fetchUrl={fetchUrl}
					actionsMenu={showActionsMenu && actionsMenu}
					columns={columns}
					originalColumns={originalColumns}
					visibleColumns={visibleColumns}
					selectionModel={selectionModel}
					setColumns={setColumns}
					setHiddenColumns={setHiddenColumns}
					setTableHiddenColumns={setTableHiddenColumns}
					filterModel={filterModel}
					onFilterModelChange={onFilterModelChange}
					onSelectionModelChange={onSelectionModelChange}
					toggleAllRowsSelected={toggleAllRowsSelected}
					showHeader={showHeader}
					showFilters={showFilters}
					showMapViewButton={showMapViewButton}
					multiSelectFilters={multiSelectFilters}
					exportName={exportName}
					exportNameTranslationObject={exportNameTranslationObject}
					sortModel={sortModel}
					requestParameters={requestParameters}
				/>

				<S.TableContainer>
					<S.TableWrapper
						ref={tableRef}
						className={rows.length === 0 || errorMsg ? 'noScroll' : ''}
						onScroll={infiniteScroll ? handleInfiniteScroll : undefined}>
						<S.Table {...getTableProps()}>
							<div className="thead">
								{headerGroups.map((headerGroup, i) => (
									<S.TableRow
										key={i}
										selectionMode={showActionsMenu || showCheckbox}
										{...headerGroup.getHeaderGroupProps()}>
										{headerGroup.headers.map((column, i) => {
											if (
												isMobile &&
												column.id !== 'rowSelection' &&
												column.id !== 'rowActions' &&
												!column.showOnMobile
											) {
												return;
											}

											return (
												<React.Fragment key={i}>
													<div
														className={`th ${column.className || ''}`}
														title={undefined}
														{...column.getHeaderProps()}>
														<div
															className="table-header"
															{...column.getSortByToggleProps()}
															title={t('clickForSort')}>
															{column.id === 'rowSelection' ||
															column.id === 'rowActions' ? (
																loading ? null : (
																	renderTableHeader(column)
																)
															) : (
																<S.HeaderContainer>
																	<Typography>
																		{t(renderTableHeader(column))}
																		{column.unit && ` (${t(column.unit)})`}
																	</Typography>
																</S.HeaderContainer>
															)}
															{column.isSorted && (
																<Icon
																	icon={column.isSortedDesc ? faAngleDown : faAngleUp}
																/>
															)}
														</div>
														{showHeaderFilters &&
															column.id !== 'rowSelection' &&
															column.id !== 'rowActions' &&
															column.showHeaderFilter !== false &&
															column.Header && (
																<S.ThFilterContainer>
																	<HeaderFilter
																		column={columns.find(
																			(c) => c.accessor === column.id
																		)}
																		fetchUrl={fetchUrl}
																		filterModel={filterModel}
																		onFilterModelChange={onFilterModelChange}
																		multiSelectFilters={multiSelectFilters}
																		requestParameters={requestParameters}
																	/>
																</S.ThFilterContainer>
															)}
													</div>
													<div
														{...column.getResizerProps()}
														className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
														onClick={(e) => {
															e.preventDefault();
															e.stopPropagation();
														}}
													/>
												</React.Fragment>
											);
										})}
									</S.TableRow>
								))}
							</div>

							{isInfiniteScrollTopLoading && (
								<Skeleton
									height="0.5rem"
									color="var(--blue)"
									style={{
										minHeight: '0.5rem'
									}}
								/>
							)}
							{!errorMsg && (
								<div
									className="tbody"
									{...getTableBodyProps()}
									onWheel={infiniteScroll ? handleInfiniteWheel : undefined}>
									{rows?.map((row, i) => {
										prepareRow(row);

										return (
											<React.Fragment key={`row_${i}`}>
												<S.TableRow
													key={`expandable_row_${i}`}
													selectionMode={showActionsMenu || showCheckbox}
													className="tr"
													{...row.getRowProps()}
													selectedContextMenuRow={selectedContextMenuRow === row.original}
													selected={Object.prototype.hasOwnProperty.call(selectedRowIds, i)}>
													{row.cells.map((cell, i) => {
														if (
															isMobile &&
															cell.column.id !== 'rowSelection' &&
															cell.column.id !== 'rowActions' &&
															!cell.column.showOnMobile
														) {
															return;
														}

														if (loading) {
															return (
																<div
																	className={`td loading ${
																		cell.column.className || ''
																	}`}
																	{...cell.getCellProps()}
																	key={i}>
																	<Skeleton />
																</div>
															);
														}

														const showMobileExpandButton = mobileExpandButtonCellId === i;

														if (cell.column.renderCell) {
															return (
																<React.Fragment key={i}>
																	<div
																		onContextMenu={(e) =>
																			handleOnContextMenu(e, row)
																		}
																		className={`td ${cell.column.className || ''} ${
																			showMobileExpandButton ? 'expandable' : ''
																		}`}
																		{...cell.getCellProps()}>
																		<Suspense fallback={<></>}>
																			{createElement(
																				DynamicListComponentMap[
																					cell.column.renderCell
																				].component,
																				{
																					value: cell.value,
																					data: row.original,
																					column: cell.column
																				}
																			)}
																		</Suspense>
																		{showMobileExpandButton && (
																			<Button
																				{...row.getToggleRowExpandedProps()}
																				icon={
																					row.isExpanded
																						? faAngleUp
																						: faAngleDown
																				}
																				variant="borderless"
																				size="x-small"
																				iconSize="x-small"
																				iconButton
																			/>
																		)}
																	</div>
																	<S.ResizerSpace
																		onContextMenu={(e) =>
																			handleOnContextMenu(e, row, true)
																		}
																	/>
																</React.Fragment>
															);
														}

														const autoTranslation =
															cell.column.translate !== undefined
																? cell.column.translate
																: [
																		FilterType.enum,
																		FilterType.enumMulti,
																		FilterType.selection,
																		FilterType.multiSelection
																	].includes(cell.column.filterType);

														return (
															<React.Fragment key={i}>
																<div
																	onContextMenu={(e) => handleOnContextMenu(e, row)}
																	className={`td ${cell.column.className || ''} ${
																		showMobileExpandButton ? 'expandable' : ''
																	}`}
																	{...cell.getCellProps()}>
																	{cell.column.filterType ? (
																		<Typography
																			autoTranslation={autoTranslation}
																			showCopyButton={cell.column.showCopyButton}>
																			{cell.value}
																		</Typography>
																	) : cell.column.Header ? (
																		<Typography
																			autoTranslation={autoTranslation}
																			showCopyButton={cell.column.showCopyButton}>
																			{cell.render('Cell')}
																		</Typography>
																	) : (
																		cell.render('Cell')
																	)}
																	{showMobileExpandButton && (
																		<Button
																			{...row.getToggleRowExpandedProps()}
																			icon={
																				row.isExpanded ? faAngleUp : faAngleDown
																			}
																			variant="borderless"
																			size="x-small"
																			iconSize="x-small"
																			iconButton
																		/>
																	)}
																</div>
																<S.ResizerSpace
																	onContextMenu={(e) =>
																		handleOnContextMenu(e, row, true)
																	}
																/>
															</React.Fragment>
														);
													})}
												</S.TableRow>
												{isMobile && row.isExpanded ? (
													<S.TableRow
														key={`expanded_row_${i}`}
														selectionMode={showActionsMenu || showCheckbox}
														className="tr expanded"
														{...row.getRowProps()}>
														<div className="td expanded">
															{row.cells.map((cell, i) => {
																if (loading) {
																	return <Skeleton key={`expanded_${i}`} />;
																}

																if (
																	cell.column.id === 'rowSelection' ||
																	cell.column.id === 'rowActions'
																)
																	return;

																if (cell.column.renderCell) {
																	return (
																		<S.ExpandedColumnContainer
																			key={`expanded_${i}`}>
																			<S.HeaderContainer className="expanded">
																				<Typography noWrap>
																					{t(renderTableHeader(cell.column))}
																					{cell.column.unit &&
																						` (${t(cell.column.unit)})`}
																				</Typography>
																			</S.HeaderContainer>
																			<Suspense fallback={<></>}>
																				{createElement(
																					DynamicListComponentMap[
																						cell.column.renderCell
																					].component,
																					{
																						value: cell.value,
																						data: row.original,
																						column: cell.column
																					}
																				)}
																			</Suspense>
																		</S.ExpandedColumnContainer>
																	);
																}

																const autoTranslation =
																	cell.column.translate !== undefined
																		? cell.column.translate
																		: [
																				FilterType.enum,
																				FilterType.enumMulti,
																				FilterType.selection,
																				FilterType.multiSelection
																			].includes(cell.column.filterType);

																return (
																	<S.ExpandedColumnContainer key={`expanded_${i}`}>
																		<S.HeaderContainer className="expanded">
																			<Typography noWrap>
																				{t(renderTableHeader(cell.column))}
																				{cell.column.unit &&
																					` (${t(cell.column.unit)})`}
																			</Typography>
																		</S.HeaderContainer>
																		{cell.column.filterType ? (
																			<Typography
																				autoTranslation={autoTranslation}
																				noWrap>
																				{cell.value}
																			</Typography>
																		) : cell.column.Header ? (
																			<Typography
																				autoTranslation={autoTranslation}
																				noWrap>
																				{cell.render('Cell')}
																			</Typography>
																		) : (
																			cell.render('Cell')
																		)}
																	</S.ExpandedColumnContainer>
																);
															})}
														</div>
													</S.TableRow>
												) : null}
											</React.Fragment>
										);
									})}
								</div>
							)}
							{showColumnCalculation && rows.length > 0 && !errorMsg && (
								<div className="tfoot">
									<DynamicListColumnSummary
										footerGroups={footerGroups}
										fetchUrl={fetchUrl}
										filters={filterModel}
										loading={loading}
									/>
								</div>
							)}
						</S.Table>
					</S.TableWrapper>
				</S.TableContainer>
				{errorMsg && (
					<NoRecordsFound
						lottieName="alert-error-icon"
						title="errorMsg.defaultFilterError"
						subTitle={errorMsg}
					/>
				)}
				{rows.length === 0 && !loading && !errorMsg ? (noRecordsFound ?? <NoRecordsFound />) : undefined}
				{isInfiniteScrollBottomLoading && (
					<S.InfiniteScrollLoadingContainer>
						<Skeleton
							height="0.5rem"
							color="var(--blue)"
							style={{
								minHeight: '0.5rem'
							}}
						/>
					</S.InfiniteScrollLoadingContainer>
				)}
				<S.Footer>
					{!infiniteScroll && (
						<DynamicListPagination
							currentPage={currentPage}
							totalCount={dataSize}
							pageSize={pageSize}
							onPageChange={onPageChange}
							setPageSize={onPageSizeChange}
							style={!showHeader && exportName ? { padding: 0 } : undefined}
						/>
					)}
					{infiniteScroll && (
						<S.InfiniteScrollPaginationContainer>
							{isInfiniteScrollBottomLoading && (
								<Icon icon={faSpinnerThird} className="fa-spin" size="small" color="var(--blue)" />
							)}
							<Typography>
								<Trans
									i18nKey={
										isInfiniteScrollBottomLoading
											? 'infiniteScrollLoadingPaginationText'
											: 'infiniteScrollLoadedPaginationText'
									}
									values={{ size: data?.length ?? 0 }}
								/>
							</Typography>
							{noMoreInfiniteScrollRecords && data?.length !== 0 && (
								<S.NoMoreInfiniteScrollRecordsContainer>
									<Typography color="var(--content-3)">infiniteScroll.NoMoreRecords</Typography>
								</S.NoMoreInfiniteScrollRecordsContainer>
							)}
						</S.InfiniteScrollPaginationContainer>
					)}
					{!showHeader && exportName && (
						<DynamicListExportButton
							columns={columns}
							filterModel={filterModel}
							sortModel={sortModel}
							exportName={exportName}
							exportNameTranslationObject={exportNameTranslationObject}
							fetchUrl={fetchUrl}
						/>
					)}
				</S.Footer>
			</S.Container>
			{!loading && isContextMenuOpen && (
				<DynamicListContextMenu
					menuItems={actionsMenu}
					row={selectedContextMenuRow}
					anchorPoint={anchorPoint}
					onClose={() => {
						setIsContextMenuOpen(false);
						setSelectedContextMenuRow(null);
						setCopyValue(null);
						setCopyColumnValues(null);
					}}
					copyValue={copyValue}
					copyColumnValues={copyColumnValues}
					isContextMenuOpen={isContextMenuOpen}
					setIsContextMenuOpen={setIsContextMenuOpen}
				/>
			)}
		</>
	);
};

export default memo(DynamicListTable);
